import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '@/lib/i18n';
import api from '@/lib/api';
import notify from '@/lib/toast';
import { useThemeStore } from '@/stores/theme.store';
import {
  DEFAULT_APPEARANCE,
  applyAppearanceToRoot,
  isAccent,
  isDensity,
  isFontSize,
  isThemeChoice,
  type AccentName,
  type Density,
  type FontSize,
  type UiPreferences,
} from '@/lib/appearance';

/**
 * POS-I226: text size, density and colour. The localStorage copy ('clearring-appearance') is only a CACHE
 * so there is no flash before /auth/me; the account (users.ui_preferences) is the source of truth and
 * overrides it on sign-in. Theme keeps its own store (it also serves the signed-out login page) but is
 * saved to the account together with the rest.
 */
interface AppearanceState {
  fontSize: FontSize;
  density: Density;
  accent: AccentName;
  /** A user change: applies at once, then saves to the account (debounced). */
  change: (patch: UiPreferences) => void;
  /** Back to the built-in choices, saved to the account. */
  reset: () => void;
  /** The account's value, from sign-in or /auth/me: applied locally, never sent back. */
  applyFromServer: (prefs: unknown) => void;
}

function applyNow(s: { fontSize: FontSize; density: Density; accent: AccentName }) {
  if (typeof document !== 'undefined') applyAppearanceToRoot(document.documentElement, s);
}

let pending: Record<string, string | null> = {};
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleSave(patch: Record<string, string | null>) {
  pending = { ...pending, ...patch };
  if (timer) clearTimeout(timer);
  timer = setTimeout(async () => {
    const body = pending;
    pending = {};
    timer = null;
    try {
      await api.patch('/users/me/preferences', body);
      notify.info(i18n.t('settings:appearanceSaved'));
    } catch {
      notify.warning(i18n.t('settings:appearanceSavedLocal'));
    }
  }, 300);
}

export const useAppearanceStore = create<AppearanceState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_APPEARANCE,
      change: (patch) => {
        const next = {
          fontSize: isFontSize(patch.fontSize) ? patch.fontSize : get().fontSize,
          density: isDensity(patch.density) ? patch.density : get().density,
          accent: isAccent(patch.accent) ? patch.accent : get().accent,
        };
        set(next);
        applyNow(next);
        const body: Record<string, string> = {};
        if (isFontSize(patch.fontSize)) body.fontSize = patch.fontSize;
        if (isDensity(patch.density)) body.density = patch.density;
        if (isAccent(patch.accent)) body.accent = patch.accent;
        if (isThemeChoice(patch.theme)) {
          useThemeStore.getState().setTheme(patch.theme);
          body.theme = patch.theme;
        }
        if (Object.keys(body).length) scheduleSave(body);
      },
      reset: () => {
        const next = { ...DEFAULT_APPEARANCE };
        set(next);
        applyNow(next);
        useThemeStore.getState().setTheme('system');
        scheduleSave({ theme: null, fontSize: null, density: null, accent: null });
      },
      applyFromServer: (prefs) => {
        const p = (prefs && typeof prefs === 'object' ? prefs : {}) as UiPreferences;
        const next = {
          fontSize: isFontSize(p.fontSize) ? p.fontSize : DEFAULT_APPEARANCE.fontSize,
          density: isDensity(p.density) ? p.density : DEFAULT_APPEARANCE.density,
          accent: isAccent(p.accent) ? p.accent : DEFAULT_APPEARANCE.accent,
        };
        set(next);
        applyNow(next);
        if (isThemeChoice(p.theme)) useThemeStore.getState().setTheme(p.theme);
      },
    }),
    {
      name: 'clearring-appearance',
      partialize: (s) => ({ fontSize: s.fontSize, density: s.density, accent: s.accent }),
      onRehydrateStorage: () => (state) => {
        if (state) applyNow(state);
      },
    },
  ),
);
